/** Theme (light/dark/system), RTL, sidebar state and toasts. */
import { create } from 'zustand';

export type Theme = 'light' | 'dark' | 'system';
export interface Toast {
  id: number;
  kind: 'success' | 'error' | 'info';
  title: string;
  message?: string;
}

interface UiState {
  theme: Theme;
  setTheme: (t: Theme) => void;
  direction: 'ltr' | 'rtl';
  setDirection: (d: 'ltr' | 'rtl') => void;
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
  commandOpen: boolean;
  setCommandOpen: (open: boolean) => void;
  toasts: Toast[];
  toast: (t: Omit<Toast, 'id'>) => void;
  dismiss: (id: number) => void;
}

const prefersDark = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;

function applyTheme(theme: Theme) {
  const root = document.documentElement;
  root.classList.toggle('dark', theme === 'dark' || (theme === 'system' && prefersDark()));
}

function applyDirection(direction: 'ltr' | 'rtl') {
  document.documentElement.setAttribute('dir', direction);
  document.documentElement.setAttribute('lang', direction === 'rtl' ? 'ar' : 'en');
}

export const useUi = create<UiState>((set, get) => ({
  theme: (localStorage.getItem('rms.theme') as Theme) ?? 'system',
  direction: (localStorage.getItem('rms.direction') as 'ltr' | 'rtl') ?? 'ltr',
  sidebarOpen: localStorage.getItem('rms.sidebar') !== 'closed',
  commandOpen: false,
  toasts: [],

  setTheme: (theme) => {
    localStorage.setItem('rms.theme', theme);
    applyTheme(theme);
    set({ theme });
  },
  setDirection: (direction) => {
    localStorage.setItem('rms.direction', direction);
    applyDirection(direction);
    set({ direction });
  },
  setSidebarOpen: (sidebarOpen) => {
    localStorage.setItem('rms.sidebar', sidebarOpen ? 'open' : 'closed');
    set({ sidebarOpen });
  },
  setCommandOpen: (commandOpen) => set({ commandOpen }),

  toast: (t) => {
    const id = Date.now() + Math.random();
    set({ toasts: [...get().toasts, { ...t, id }] });
    setTimeout(() => get().dismiss(id), t.kind === 'error' ? 7000 : 4000);
  },
  dismiss: (id) => set({ toasts: get().toasts.filter((t) => t.id !== id) }),
}));

// Apply persisted appearance on first import.
applyTheme(useUi.getState().theme);
applyDirection(useUi.getState().direction);
